import type * as Monaco from 'monaco-editor/esm/vs/editor/editor.api';

export interface LanguageConfig {
    id: string;
    extensions: string[];
    config: Monaco.languages.LanguageConfiguration;
    monarchTokens: Monaco.languages.IMonarchLanguage;
    diagnostics?: boolean;
    completion?: boolean;
    formatting?: boolean;
}

export const languageConfigurations: { [key: string]: LanguageConfig } = {
    motoko: {
        id: 'motoko',
        extensions: ['.mo'],
        diagnostics: true,
        completion: true,
        formatting: true,
        config: {
            comments: {
                lineComment: '//',
                blockComment: ['/*', '*/']
            },
            brackets: [
                ['{', '}'],
                ['[', ']'],
                ['(', ')']
            ],
            autoClosingPairs: [
                {open: '{', close: '}'},
                {open: '[', close: ']'},
                {open: '(', close: ')'},
                {open: '"', close: '"'},
                {open: "'", close: "'"}
            ],
            surroundingPairs: [
                {open: '{', close: '}'},
                {open: '[', close: ']'},
                {open: '(', close: ')'},
                {open: '<', close: '>'},
                {open: '"', close: '"'},
                {open: "'", close: "'"}
            ]
        },
        monarchTokens: {
            defaultToken: '',
            tokenPostfix: '.mo',

            keywords: [
                // Actor and module keywords
                'actor', 'module', 'import',

                // Method modifiers
                'query', 'composite', 'public', 'private', 'shared', 'system',

                // Function keywords
                'func', 'return', 'async', 'await',

                // Type declarations
                'type', 'class', 'object', 'flexible', 'stable',

                // Control flow
                'if', 'else', 'for', 'while', 'loop', 'in', 'break', 'continue',
                'switch', 'case', 'try', 'catch', 'throw', 'debug',

                // Operators as keywords
                'and', 'or', 'not',

                // Constants
                'null', 'true', 'false'
            ],

            typeKeywords: [
                // Base types
                'Text', 'Principal', 'Bool', 'Nat', 'Int', 'Float',
                'Char', 'Error',

                // Advanced types
                'Any', 'None', 'Null',

                // Container types
                'Array', 'Buffer', 'Blob',

                // Special types
                'async', 'never', 'void'
            ],

            operators: [
                '=', '>', '<', '!', '~', '?', ':',
                '==', '<=', '>=', '!=', '&&', '||',
                '+', '-', '*', '/', '&', '|', '^', '%',
                '+=', '-=', '*=', '/=', '&=', '|=', '^='
            ],

            symbols: /[=><!~?:&|+\-*\/\^%]+/,

            tokenizer: {
                root: [
                    // Identifiers and keywords
                    [/[a-zA-Z_$][\w$]*/, {
                        cases: {
                            '@typeKeywords': 'type',
                            '@keywords': 'keyword',
                            '@default': 'identifier'
                        }
                    }],

                    // Whitespace
                    {include: '@whitespace'},

                    // Delimiters and operators
                    [/[{}()\[\]]/, '@brackets'],
                    [/[<>](?!@symbols)/, '@brackets'],
                    [/@symbols/, {
                        cases: {
                            '@operators': 'operator',
                            '@default': ''
                        }
                    }],

                    // Numbers
                    [/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
                    [/0[xX][0-9a-fA-F]+/, 'number.hex'],
                    [/\d+/, 'number'],

                    // Delimiter
                    [/[;,.]/, 'delimiter'],

                    // Strings
                    [/"([^"\\]|\\.)*$/, 'string.invalid'],
                    [/"/, {token: 'string.quote', bracket: '@open', next: '@string'}]
                ],

                comment: [
                    [/[^\/*]+/, 'comment'],
                    [/\*\//, 'comment', '@pop'],
                    [/[\/*]/, 'comment']
                ],

                string: [
                    [/[^\\"]+/, 'string'],
                    [/\\./, 'string.escape'],
                    [/"/, {token: 'string.quote', bracket: '@close', next: '@pop'}]
                ],

                whitespace: [
                    [/[ \t\r\n]+/, 'white'],
                    [/\/\*/, 'comment', '@comment'],
                    [/\/\/.*$/, 'comment'],
                ]
            }
        }
    },

    typescript: {
        id: 'typescript',
        extensions: ['.ts', '.tsx'],
        diagnostics: true,
        completion: true,
        formatting: true,
        config: {
            comments: {
                lineComment: '//',
                blockComment: ['/*', '*/']
            },
            brackets: [
                ['{', '}'],
                ['[', ']'],
                ['(', ')']
            ],
            autoClosingPairs: [
                {open: '{', close: '}'},
                {open: '[', close: ']'},
                {open: '(', close: ')'},
                {open: "'", close: "'", notIn: ['string', 'comment']},
                {open: '"', close: '"', notIn: ['string']},
                {open: '`', close: '`', notIn: ['string', 'comment']}
            ]
        },
        monarchTokens: {
            defaultToken: '',
            tokenPostfix: '.ts',
            keywords: [
                'break', 'case', 'catch', 'class', 'const', 'continue', 'debugger',
                'default', 'delete', 'do', 'else', 'enum', 'export', 'extends', 'false',
                'finally', 'for', 'function', 'if', 'import', 'in', 'instanceof', 'new',
                'null', 'return', 'super', 'switch', 'this', 'throw', 'true', 'try',
                'typeof', 'var', 'void', 'while', 'with', 'implements', 'interface',
                'let', 'package', 'private', 'protected', 'public', 'static', 'yield'
            ],
            typeKeywords: [
                'any', 'boolean', 'number', 'object', 'string', 'undefined',
                'never', 'void', 'symbol', 'unknown', 'bigint'
            ],
            tokenizer: {
                root: [
                    [/[a-zA-Z_$][\w$]*/, {
                        cases: {
                            '@typeKeywords': 'type',
                            '@keywords': 'keyword',
                            '@default': 'identifier'
                        }
                    }],
                    [/[ \t\r\n]+/, 'white'],
                    [/\/\*/, 'comment', '@comment'],
                    [/\/\/.*$/, 'comment'],
                    [/[{}()\[\]]/, '@brackets'],
                    [/"([^"\\]|\\.)*$/, 'string.invalid'],
                    [/'([^'\\]|\\.)*$/, 'string.invalid'],
                    [/"/, 'string', '@string_double'],
                    [/'/, 'string', '@string_single'],
                    [/`/, 'string', '@string_backtick'],
                    [/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
                    [/0[xX][0-9a-fA-F]+/, 'number.hex'],
                    [/\d+/, 'number'],
                ],
                comment: [
                    [/[^\/*]+/, 'comment'],
                    [/\*\//, 'comment', '@pop'],
                    [/[\/*]/, 'comment']
                ],
                string_double: [
                    [/[^\\"]+/, 'string'],
                    [/\\./, 'string.escape'],
                    [/"/, 'string', '@pop']
                ],
                string_single: [
                    [/[^\\']+/, 'string'],
                    [/\\./, 'string.escape'],
                    [/'/, 'string', '@pop']
                ],
                string_backtick: [
                    [/[^\\`$]+/, 'string'],
                    [/\${/, {token: 'delimiter.bracket', next: '@bracketCounting'}],
                    [/\\./, 'string.escape'],
                    [/`/, 'string', '@pop']
                ],
                bracketCounting: [
                    [/\{/, 'delimiter.bracket', '@bracketCounting'],
                    [/\}/, 'delimiter.bracket', '@pop'],
                    {include: 'root'}
                ]
            }
        }
    },

    javascript: {
        id: 'javascript',
        extensions: ['.js', '.jsx'],
        diagnostics: true,
        completion: true,
        formatting: true,
        config: {
            comments: {
                lineComment: '//',
                blockComment: ['/*', '*/']
            },
            brackets: [
                ['{', '}'],
                ['[', ']'],
                ['(', ')']
            ],
            autoClosingPairs: [
                {open: '{', close: '}'},
                {open: '[', close: ']'},
                {open: '(', close: ')'},
                {open: "'", close: "'", notIn: ['string', 'comment']},
                {open: '"', close: '"', notIn: ['string']},
                {open: '`', close: '`', notIn: ['string', 'comment']}
            ]
        },
        monarchTokens: {
            defaultToken: '',
            tokenPostfix: '.js',
            keywords: [
                'break', 'case', 'catch', 'class', 'const', 'continue', 'debugger',
                'default', 'delete', 'do', 'else', 'export', 'extends', 'false',
                'finally', 'for', 'function', 'if', 'import', 'in', 'instanceof',
                'new', 'null', 'return', 'super', 'switch', 'this', 'throw', 'true',
                'try', 'typeof', 'var', 'void', 'while', 'with', 'let'
            ],
            tokenizer: {
                root: [
                    [/[a-zA-Z_$][\w$]*/, {
                        cases: {
                            '@keywords': 'keyword',
                            '@default': 'identifier'
                        }
                    }],
                    [/[ \t\r\n]+/, 'white'],
                    [/\/\*/, 'comment', '@comment'],
                    [/\/\/.*$/, 'comment'],
                    [/[{}()\[\]]/, '@brackets'],
                    [/"([^"\\]|\\.)*$/, 'string.invalid'],
                    [/'([^'\\]|\\.)*$/, 'string.invalid'],
                    [/"/, 'string', '@string_double'],
                    [/'/, 'string', '@string_single'],
                    [/`/, 'string', '@string_backtick'],
                    [/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
                    [/0[xX][0-9a-fA-F]+/, 'number.hex'],
                    [/\d+/, 'number'],
                ],
                comment: [
                    [/[^\/*]+/, 'comment'],
                    [/\*\//, 'comment', '@pop'],
                    [/[\/*]/, 'comment']
                ],
                string_double: [
                    [/[^\\"]+/, 'string'],
                    [/\\./, 'string.escape'],
                    [/"/, 'string', '@pop']
                ],
                string_single: [
                    [/[^\\']+/, 'string'],
                    [/\\./, 'string.escape'],
                    [/'/, 'string', '@pop']
                ],
                string_backtick: [
                    [/[^\\`$]+/, 'string'],
                    [/\${/, {token: 'delimiter.bracket', next: '@bracketCounting'}],
                    [/\\./, 'string.escape'],
                    [/`/, 'string', '@pop']
                ],
                bracketCounting: [
                    [/\{/, 'delimiter.bracket', '@bracketCounting'],
                    [/\}/, 'delimiter.bracket', '@pop'],
                    {include: 'root'}
                ]
            }
        }
    },


    css: {
        id: 'css',
        extensions: ['.css'],
        diagnostics: true,
        completion: true,
        formatting: true,
        config: {
            brackets: [
                ['{', '}'],
                ['[', ']'],
                ['(', ')']
            ],
            autoClosingPairs: [
                {open: '{', close: '}'},
                {open: '[', close: ']'},
                {open: '(', close: ')'},
                {open: "'", close: "'", notIn: ['string', 'comment']},
                {open: '"', close: '"', notIn: ['string']}
            ]
        },
        monarchTokens: {
            defaultToken: '',
            tokenPostfix: '.css',
            tokenizer: {
                root: [
                    [/[a-zA-Z_][\w-]*/, 'tag'],
                    [/[.#][a-zA-Z_][\w-]*/, 'tag'],
                    [/[a-zA-Z-]+(?=\s*:)/, 'attribute.name'],
                    [/[0-9]+(em|px|rem|%|vh|vw|pt|pc)/, 'number'],
                    [/#[0-9a-fA-F]{3,6}/, 'string'],
                    [/\/\*/, 'comment', '@comment'],
                    [/[{}]/, '@brackets'],
                    [/"([^"\\]|\\.)*$/, 'string.invalid'],
                    [/'([^'\\]|\\.)*$/, 'string.invalid'],
                    [/"/, 'string', '@string_double'],
                    [/'/, 'string', '@string_single']
                ],
                comment: [
                    [/[^\/*]+/, 'comment'],
                    [/\*\//, 'comment', '@pop'],
                    [/[\/*]/, 'comment']
                ],
                string_double: [
                    [/[^\\"]+/, 'string'],
                    [/\\./, 'string.escape'],
                    [/"/, 'string', '@pop']
                ],
                string_single: [
                    [/[^\\']+/, 'string'],
                    [/\\./, 'string.escape'],
                    [/'/, 'string', '@pop']
                ]
            }
        }
    },


    html: {
        id: 'html',
        extensions: ['.html'],
        diagnostics: true,
        completion: true,
        formatting: true,
        config: {
            brackets: [
                ['{', '}'],
                ['[', ']'],
                ['(', ')'],
                ['<', '>']
            ],
            autoClosingPairs: [
                {open: '{', close: '}'},
                {open: '[', close: ']'},
                {open: '(', close: ')'},
                {open: "'", close: "'", notIn: ['string', 'comment']},
                {open: '"', close: '"', notIn: ['string']},
                {open: '<', close: '>'}
            ],
            surroundingPairs: [
                {open: '{', close: '}'},
                {open: '[', close: ']'},
                {open: '(', close: ')'},
                {open: '<', close: '>'},
                {open: "'", close: "'"},
                {open: '"', close: '"'}
            ]
        },
        monarchTokens: {
            defaultToken: '',
            tokenPostfix: '.html',
            tokenizer: {
                root: [
                    [/<!DOCTYPE/, 'metatag', '@doctype'],
                    [/<!--/, 'comment', '@comment'],
                    [/(<)((?:[\w\-]+:)?[\w\-]+)(\s*)(\/>)/, ['delimiter', 'tag', '', 'delimiter']],
                    [/(<)(script)/, ['delimiter', {token: 'tag', next: '@script'}]],
                    [/(<)(style)/, ['delimiter', {token: 'tag', next: '@style'}]],
                    [/(<)((?:[\w\-]+:)?[\w\-]+)/, ['delimiter', {token: 'tag', next: '@otherTag'}]],
                    [/(<\/)((?:[\w\-]+:)?[\w\-]+)/, ['delimiter', {token: 'tag', next: '@otherTag'}]],
                    [/</, 'delimiter'],
                    [/[^<]+/, 'text']  // Fixed token value
                ],
                doctype: [
                    [/[^>]+/, 'metatag.content'],
                    [/>/, 'metatag', '@pop']
                ],
                comment: [
                    [/-->/, 'comment', '@pop'],
                    [/[^-]+/, 'comment.content'],
                    [/./, 'comment.content']
                ],
                otherTag: [
                    [/\/?>/, 'delimiter', '@pop'],
                    [/"([^"]*)"/, 'attribute.value'],
                    [/'([^']*)'/, 'attribute.value'],
                    [/[\w\-]+/, 'attribute.name'],
                    [/=/, 'delimiter'],
                    [/[ \t\r\n]+/, 'whitespace']  // Fixed token value
                ],
                script: [
                    [/type/, 'attribute.name', '@scriptAfterType'],
                    [/"([^"]*)"/, 'attribute.value'],
                    [/'([^']*)'/, 'attribute.value'],
                    [/[\w\-]+/, 'attribute.name'],
                    [/=/, 'delimiter'],
                    [/>/, {token: 'delimiter', next: '@scriptEmbedded', nextEmbedded: 'text/javascript'}],
                    [/[ \t\r\n]+/, 'whitespace'],  // Fixed token value
                    [/(<\/)(script\s*)(>)/, ['delimiter', 'tag', {token: 'delimiter', next: '@pop'}]]
                ],
                scriptAfterType: [
                    [/=/, 'delimiter', '@scriptAfterTypeEquals'],
                    [/>/, {token: 'delimiter', next: '@scriptEmbedded', nextEmbedded: 'text/javascript'}],
                    [/[ \t\r\n]+/, 'whitespace'],  // Fixed token value
                    [/<\/script\s*>/, {token: '@rematch', next: '@pop'}]
                ],
                scriptAfterTypeEquals: [
                    [/"([^"]*)"/, {token: 'attribute.value', switchTo: '@scriptWithCustomType.$1'}],
                    [/'([^']*)'/, {token: 'attribute.value', switchTo: '@scriptWithCustomType.$1'}],
                    [/>/, {token: 'delimiter', next: '@scriptEmbedded', nextEmbedded: 'text/javascript'}],
                    [/[ \t\r\n]+/, 'whitespace']  // Fixed token value
                ],
                style: [
                    [/type/, 'attribute.name', '@styleAfterType'],
                    [/"([^"]*)"/, 'attribute.value'],
                    [/'([^']*)'/, 'attribute.value'],
                    [/[\w\-]+/, 'attribute.name'],
                    [/=/, 'delimiter'],
                    [/>/, {token: 'delimiter', next: '@styleEmbedded', nextEmbedded: 'text/css'}],
                    [/[ \t\r\n]+/, 'whitespace']  // Fixed token value
                ],
                styleAfterType: [
                    [/=/, 'delimiter', '@styleAfterTypeEquals'],
                    [/>/, {token: 'delimiter', next: '@styleEmbedded', nextEmbedded: 'text/css'}],
                    [/[ \t\r\n]+/, 'whitespace'],  // Fixed token value
                    [/<\/style\s*>/, {token: '@rematch', next: '@pop'}]
                ],
                styleAfterTypeEquals: [
                    [/"([^"]*)"/, {token: 'attribute.value', switchTo: '@styleWithCustomType.$1'}],
                    [/'([^']*)'/, {token: 'attribute.value', switchTo: '@styleWithCustomType.$1'}],
                    [/>/, {token: 'delimiter', next: '@styleEmbedded', nextEmbedded: 'text/css'}],
                    [/[ \t\r\n]+/, 'whitespace']  // Fixed token value
                ],
                styleWithCustomType: [
                    [/>/, {token: 'delimiter', next: '@styleEmbedded.$S2', nextEmbedded: '$S2'}],
                    {include: '@otherTag'}
                ],
                styleEmbedded: [
                    [/<\/style/, {token: '@rematch', next: '@pop', nextEmbedded: '@pop'}],
                    [/[^<]+/, 'text']  // Fixed token value
                ],
                scriptEmbedded: [
                    [/<\/script/, {token: '@rematch', next: '@pop', nextEmbedded: '@pop'}],
                    [/[^<]+/, 'text']  // Fixed token value
                ]
            }
        }
    }
};